import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { scimApi, ScimTokenSummary } from '@/lib/scim';
import { isPlanGateError } from '@/lib/planGate';
import { UpgradeWall } from '@/components/app/UpgradeWall';
import { AppShell } from '@/components/app/AppShell';
import { useCurrentUser } from '@/hooks/useAuth';

export function ScimAdminPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const { data: user, isLoading: userLoading } = useCurrentUser();
  if (!userLoading && user?.isAdmin !== true) {
    return <Navigate to={`/app/workspace/${wsId}`} replace />;
  }
  // Scope to the current workspace's company (not companies[0]).
  const { data: workspace } = useQuery({
    queryKey: ['workspace', wsId],
    queryFn: async () => (await api.get<{ companyId: string }>(`/api/workspaces/${wsId}`)).data,
    enabled: !!wsId,
  });
  const companyId = workspace?.companyId;

  const { data: tokens = [], isLoading, error: listError } = useQuery({
    queryKey: ['scim-tokens', companyId],
    queryFn: () => scimApi.list(companyId!),
    enabled: !!companyId,
    retry: false,
  });

  const [displayName, setDisplayName] = useState('');
  const [revealed, setRevealed] = useState<string | null>(null);

  const mint = useMutation({
    mutationFn: () => scimApi.mint(companyId!, displayName),
    onSuccess: (r) => {
      setRevealed(r.token);
      setDisplayName('');
      qc.invalidateQueries({ queryKey: ['scim-tokens', companyId] });
    },
  });

  const revoke = useMutation({
    mutationFn: (tokenId: string) => scimApi.revoke(companyId!, tokenId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scim-tokens', companyId] }),
  });

  return (
    <AppShell title={t('scim.title')} subtitle={t('scim.subtitle')}>
<main className="mx-auto max-w-4xl px-6 py-10 space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-ink-900 tracking-tight">{t('scim.title')}</h1>
          <p className="mt-1 text-ink-600">{t('scim.subtitle')}</p>
        </div>

        <div className="rounded-xl border border-ink-200 bg-white px-4 py-3 text-sm text-ink-700">
          <span className="text-ink-500">{t('scim.endpoint')}:</span>{' '}
          <span className="num font-semibold">{scimApi.endpointUrl()}</span>
        </div>

        <section className="rounded-2xl border border-ink-200 bg-white p-5">
          <h2 className="text-base font-semibold text-ink-900">{t('scim.mint')}</h2>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="block flex-1 min-w-[200px]">
              <span className="block text-xs text-ink-500 mb-1">{t('scim.token_name')}</span>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t('scim.token_name_placeholder')}
                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm"
              />
            </label>
            <button
              onClick={() => mint.mutate()}
              disabled={mint.isPending || !displayName.trim() || !companyId}
              className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-60"
            >
              {mint.isPending ? t('scim.minting') : t('scim.mint')}
            </button>
          </div>

          {revealed && (
            <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-4">
              <p className="text-xs text-emerald-700 font-semibold mb-2">{t('scim.token_revealed')}</p>
              <code className="num block break-all bg-white px-3 py-2 rounded border border-emerald-200 text-xs">{revealed}</code>
              <button
                onClick={() => { void navigator.clipboard?.writeText(revealed); }}
                className="mt-2 text-xs text-accent-600 hover:underline"
              >
                {t('scim.token_copy')}
              </button>
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-ink-200 bg-white p-5">
          <h2 className="text-base font-semibold text-ink-900">{t('scim.tokens')}</h2>
          {isLoading ? (
            <div className="mt-3 text-sm text-ink-500">{t('common.loading')}</div>
          ) : listError ? (
            isPlanGateError(listError) ? (
              <div className="mt-3">
                <UpgradeWall wsId={wsId!} requiredTier="enterprise" featureName={t('scim.feature_name', { defaultValue: 'SCIM provisioning' })} />
              </div>
            ) : (
              <div className="mt-3 rounded-lg border border-warn-500/30 bg-warn-500/10 text-warn-700 px-3 py-2 text-sm">
                {(listError as Error).message}
              </div>
            )
          ) : tokens.length === 0 ? (
            <p className="mt-3 text-sm text-ink-500">{t('scim.empty')}</p>
          ) : (
            <ul className="mt-3 divide-y divide-ink-100">
              {tokens.map((tok) => <TokenRow key={tok.id} token={tok} onRevoke={() => revoke.mutate(tok.id)} t={t} />)}
            </ul>
          )}
        </section>
      </main>
    </AppShell>
  );
}

function TokenRow({ token, onRevoke, t }: { token: ScimTokenSummary; onRevoke: () => void; t: (k: string) => string }) {
  return (
    <li className="py-3 flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-ink-900">{token.displayName}</p>
        <p className="text-xs num text-ink-500">{new Date(token.createdAt).toLocaleString()} · {token.id.slice(0, 8)}</p>
      </div>
      <button onClick={onRevoke} className="text-xs text-warn-600 hover:underline">{t('scim.revoke')}</button>
    </li>
  );
}
